import { MessageSendError } from './MessageSendError';
import colors from '../colors';
import { styledWithProps } from '../../util/styles';
import * as React from 'react';
import styled, { StyledFunction } from 'styled-components';
import { lighten } from 'polished';
import Linkify from 'react-linkify';

import { MessageWithMetadata } from '../../typedefs/store';
import AnonymousAvatar from './AnonymousAvatar';
import Attachment from './Attachments/Attachment';
import ChoiceButtonList from './ChoiceButtonList';
import MessageLinkContainer from './MessageLinkContainer';

interface Props {
    message: MessageWithMetadata;
    isMostRecentMessage: boolean;
    showAvatars?: boolean;
    avatar?: string;
    send: (message: any, tempId?: string) => void;
    ended: boolean;
    openLinksExternally: boolean;
}

const Message: React.SFC<Props> = ({
    message,
    isMostRecentMessage,
    send,
    showAvatars = false,
    avatar,
    ended,
    openLinksExternally,
}) => (
    <Wrapper>
        <InnerWrapper type={message.fromType}>
            {showAvatars && (
                <AvatarWrapper type={message.fromType}>
                    {message.avatar ? <Avatar src={message.avatar} /> : <AnonymousAvatar />}
                </AvatarWrapper>
            )}
            <SendingIndicator
                isSending={message.sending || false}
                hasError={message.hasSendError || false}
            >
                <Bubble type={message.fromType}>
                    <Linkify component={MessageLinkContainer}>{message.text}</Linkify>
                    {!ended &&
                        message.buttons &&
                        isMostRecentMessage && (
                            <ChoiceButtonList buttons={message.buttons} onClick={send} />
                        )}
                    {message.attachments &&
                        message.attachments.length > 0 && (
                            <AttachmentListWrapper>
                                {message.attachments.map((attachment, i) => (
                                    <Attachment
                                        key={i}
                                        attachment={attachment}
                                        openLinksExternally={openLinksExternally}
                                    />
                                ))}
                            </AttachmentListWrapper>
                        )}
                </Bubble>
            </SendingIndicator>
        </InnerWrapper>
        {message.hasSendError && (
            <MessageSendError
                message={message}
                retrySend={(message: MessageWithMetadata) =>
                    send(message.text, message.id as string)}
            />
        )}
    </Wrapper>
);

export default Message;

const div = styledWithProps<{ type: string }>(styled.div);
const Wrapper = styled.div`
    margin-bottom: ${props => props.theme.messageBubble.marginBottom};
    &:last-child {
        margin-bottom: 0;
    }
`;

const InnerWrapper = div`
    display: flex;
    align-items: flex-end;
    justify-content: ${props => (props.type === 'visitor' ? 'flex-end' : 'flex-start')};
`;

const AvatarWrapper = div`
    flex-grow: 0;
    flex-shrink: 0;
    margin-right: ${props => (props.type === 'visitor' ? '0' : '10px')};
    margin-left: ${props => (props.type === 'visitor' ? '10px' : '0')};
    height: 30px;
    width: 30px;
    order: ${props => (props.type === 'visitor' ? 1 : 0)};
`;

const Avatar = styled.img`
    width: 30px;
    height: 30px;
    border-radius: 50%;
`;

const Bubble = div`
    flex: 1; /* solves IE11 bug with image max-width in flex containers */
    border-radius: ${props => (props.type === 'visitor' ? '10px 10px 0 10px' : '10px 10px 10px 0')};
    background-color: ${props =>
        props.type === 'visitor'
            ? props.theme.messageBubble.visitor.bgColor
            : props.theme.messageBubble.agent.bgColor};
    color: ${props =>
        props.type === 'visitor'
            ? props.theme.messageBubble.visitor.fgColor
            : props.theme.messageBubble.agent.fgColor};
    a, a:hover, a:active, a:focus, a:visited {
        color: ${props =>
            props.type === 'visitor'
                ? props.theme.messageBubble.visitor.fgColor
                : props.theme.messageBubble.agent.fgColor};
    }
    a:hover {
        text-decoration: none;
    }
    margin-right: ${props => (props.type === 'visitor' ? '6px' : '0')};
    margin-left: ${props => (props.type === 'visitor' ? '0' : '6px')};
    padding: 10px;
    position: relative;
    word-break: break-word;
    font-weight: ${props => props.theme.fontWeight};

    &:before {
        content: ' ';
        position: absolute;
        width: 0;
        height: 0;
        right: ${props => (props.type === 'visitor' ? '-6px' : 'auto')};
        left:  ${props => (props.type === 'visitor' ? 'auto' : '-6px')};
        bottom: 0;
        border: 3px solid;
        border-color: ${props =>
            props.type === 'visitor'
                ? `transparent transparent ${props.theme.messageBubble.visitor.bgColor} ${props
                      .theme.messageBubble.visitor.bgColor}`
                : `transparent ${props.theme.messageBubble.agent.bgColor} ${props.theme
                      .messageBubble.agent.bgColor} transparent`};
    }
`;

const AttachmentListWrapper = styled.div`
    & > div {
        margin-bottom: 20px;
        &:last-child {
            margin-bottom: 0;
        }
    }
`;

// marks in-flight messages or messages with errors with slight opacity
const SendingIndicator = styledWithProps<{ isSending: boolean; hasError: boolean }>(styled.div)`
    opacity: ${props => (props.isSending || props.hasError ? 0.7 : 1)};
`;



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/Shared/MessageMessage.tsx